import React, { useState } from 'react';
import { 
  Bell, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Calendar,
  Plus,
  Trash2,
  Eye
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import { useAdminNotifications } from '../../hooks/useAdminNotifications';
import { formatDate, formatTime } from '../../utils/dateUtils';
import AdminBroadcastModal from '../../components/admin/AdminBroadcastModal';
import AdminNotificationDetailModal from '../../components/admin/AdminNotificationDetailModal';

const AdminNotifications = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [detailNotification, setDetailNotification] = useState(null);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const {
    notifications,
    isLoading,
    error,
    filters,
    readStatusOptions,
    sentStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics,
    deleteNotification,
    isDeleting,
    refetch
  } = useAdminNotifications();

  const handleRowSelect = (selectedRows) => {
    setSelectedNotifications(selectedRows);
  };

  const handleBulkDelete = async () => {
    if (selectedNotifications.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedNotifications.length} notification(s)?`)) {
      for (const notification of selectedNotifications) {
        await deleteNotification(notification.id);
      }
      setSelectedNotifications([]);
    }
  };

  // Define table columns
  const columns = [
    {
      key: 'user',
      label: 'User',
      sortable: true,
      width: 'w-48',
      render: (notification) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {notification.user?.name || 'All Users'}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {notification.user?.email || ''}
          </p>
        </div>
      )
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      width: 'w-32',
      render: (notification) => (
        <span className="text-sm font-medium text-gray-900 capitalize">
          {notification.type || 'N/A'}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      width: 'w-48',
      render: (notification) => (
        <p className="text-sm font-medium text-gray-900 truncate max-w-[120px]" title={notification.title}>
          {notification.title?.length > 20 ? notification.title.substring(0, 20) + '...' : notification.title || 'N/A'}
        </p>
      )
    },
    {
      key: 'message',
      label: 'Message',
      sortable: false,
      width: 'w-64',
      render: (notification) => (
        <p className="text-sm text-gray-900 truncate max-w-[150px]" title={notification.message}>
          {notification.message?.length > 25 ? notification.message.substring(0, 25) + '...' : notification.message || 'N/A'}
        </p>
      )
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      width: 'w-40',
      render: (notification) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(notification.created_at, 'medium')}
            </div>
            <div className="text-xs text-gray-500">
              {formatTime(notification.created_at)}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      width: 'w-32',
      render: (notification) => (
        <div className="flex flex-col gap-1">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            notification.is_read 
              ? 'bg-green-100 text-green-800' 
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {notification.is_read ? 'Read' : 'Unread'}
          </span>
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            notification.is_sent 
              ? 'bg-blue-100 text-blue-800' 
              : 'bg-gray-100 text-gray-800'
          }`}>
            {notification.is_sent ? 'Sent' : 'Pending'}
          </span>
        </div>
      )
    }
  ];

  const handleViewNotification = (notification) => {
    setDetailNotification(notification);
  };

  const handleDeleteFromModal = (id) => {
    deleteNotification(id, {
      onSuccess: () => {
        setDetailNotification(null);
        setSelectedNotifications((prev) => prev.filter((n) => n.id !== id));
      },
    });
  };

  // Define table actions
  const actions = [
    {
      label: 'View',
      icon: Eye,
      variant: 'default',
      onClick: handleViewNotification,
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (notification) => {
        if (window.confirm('Are you sure you want to delete this notification?')) {
          deleteNotification(notification.id);
        }
      }
    }
  ];

  const { totalNotifications, unreadNotifications, sentNotifications } = statistics;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Notifications</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load notifications. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Bell className="w-6 h-6" />
                Notifications
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage and send platform notifications
              </p>
            </div>
            <div className="flex items-center gap-2">
              {selectedNotifications.length > 0 && (
                <Button
                  onClick={handleBulkDelete}
                  variant="danger"
                  className="flex items-center gap-2"
                  disabled={isDeleting}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete ({selectedNotifications.length})
                </Button>
              )}
              <Button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Notification
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Notifications</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{totalNotifications}</p>
              </div>
              <Bell className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unread</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{unreadNotifications}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Sent</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{sentNotifications}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <SearchSelect
              label="Read Status"
              value={filters.is_read}
              onChange={(value) => handleFilterChange('is_read', value)}
              options={readStatusOptions}
            />
            <SearchSelect
              label="Sent Status"
              value={filters.is_sent}
              onChange={(value) => handleFilterChange('is_sent', value)}
              options={sentStatusOptions}
            />
            <DatePicker
              label="From Date"
              value={filters.date_from}
              onChange={(value) => handleFilterChange('date_from', value)}
            />
            <DatePicker
              label="To Date"
              value={filters.date_to}
              onChange={(value) => handleFilterChange('date_to', value)}
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <DataTable
            data={notifications}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            selectable={true}
            onRowSelect={handleRowSelect}
            onRowClick={handleViewNotification}
            searchPlaceholder="Search notifications by title, message, type, or user..."
            onSearch={handleSearch}
            emptyMessage="No notifications found"
          />
        </div>
      </div>

      {/* Broadcast Modal */}
      <AdminBroadcastModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          // Refetch notifications after broadcast is sent
          refetch();
        }}
      />

      <AdminNotificationDetailModal
        notification={detailNotification}
        onClose={() => setDetailNotification(null)}
        onDelete={handleDeleteFromModal}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default AdminNotifications;

